import { useEffect, useState } from 'react';
import ErrorMessage from '../components/ErrorMessage';
import LoadingState from '../components/LoadingState';
import { useAuth } from '../contexts/AuthContext';
import { getColumnMapping, getTatSettings, saveColumnMapping, saveTatSettings } from '../services/settingsService';
import { COLUMN_LABELS, DEFAULT_COLUMN_MAPPING, DEFAULT_TAT_SETTINGS } from '../utils/constants';
import { normalizeServiceType } from '../utils/normalization';

export default function SettingsPage() {
  const { user } = useAuth();
  const [mapping, setMapping] = useState(DEFAULT_COLUMN_MAPPING);
  const [dashboardDateKey, setDashboardDateKey] = useState('milestone_end_date');
  const [tatSettings, setTatSettings] = useState(DEFAULT_TAT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newServiceType, setNewServiceType] = useState('');
  const [newTatHours, setNewTatHours] = useState('24');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [mappingResult, tatResult] = await Promise.all([
          getColumnMapping(),
          getTatSettings(),
        ]);
        if (!cancelled) {
          setMapping(mappingResult.mapping);
          setDashboardDateKey(mappingResult.dashboardDateKey);
          setTatSettings(tatResult);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function updateColumn(key, value) {
    setMapping((current) => ({ ...current, [key]: value.toUpperCase().trim() }));
  }

  function updateServiceTat(serviceType, value) {
    setTatSettings((current) => ({
      ...current,
      serviceTatHours: {
        ...current.serviceTatHours,
        [serviceType]: value === '' ? '' : Number(value),
      },
    }));
  }


  function addServiceTat() {
    const serviceType = normalizeServiceType(newServiceType);
    if (!serviceType) return;
    setTatSettings((current) => ({
      ...current,
      serviceTatHours: {
        ...current.serviceTatHours,
        [serviceType]: Number(newTatHours || current.defaultTatHours || 24),
      },
    }));
    setNewServiceType('');
    setNewTatHours('24');
  }

  function removeServiceTat(serviceType) {
    setTatSettings((current) => {
      const next = { ...(current.serviceTatHours || {}) };
      delete next[serviceType];
      return { ...current, serviceTatHours: next };
    });
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const nextMapping = {
        ...mapping,
        dashboard_date: mapping[dashboardDateKey] || mapping.dashboard_date,
      };
      const cleanedTat = {
        defaultTatHours: Number(tatSettings.defaultTatHours || DEFAULT_TAT_SETTINGS.defaultTatHours),
        uploadRetentionCount: Math.max(1, Number(tatSettings.uploadRetentionCount || 2)),
        serviceTatHours: Object.entries(tatSettings.serviceTatHours || {})
          .filter(([, value]) => value !== '' && value !== null && value !== undefined)
          .reduce((acc, [key, value]) => {
            const normalizedKey = normalizeServiceType(key);
            if (normalizedKey) acc[normalizedKey] = Number(value);
            return acc;
          }, {}),
      };
      await Promise.all([
        saveColumnMapping(nextMapping, dashboardDateKey, user.id),
        saveTatSettings(cleanedTat, user.id),
      ]);
      setSuccess('Settings saved. Column changes apply to new uploads. TAT changes apply immediately to dashboard calculations.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState message="Loading settings..." />;

  const editableKeys = Object.keys(DEFAULT_COLUMN_MAPPING).filter((key) => key !== 'dashboard_date');
  const dateOptions = ['milestone_end_date', 'milestone_start_date', 'project_end_date', 'project_start_date', 'created_date'];

  return (
    <div className="page-stack">
      <div className="page-title-row">
        <div>
          <h2>Settings / Column Mapping & TAT</h2>
          <p>Configure Excel source columns and promised TAT thresholds used by the dashboard.</p>
        </div>
      </div>
      <ErrorMessage message={error} />
      {success && <div className="success-message">{success}</div>}
      <form className="panel mapping-form" onSubmit={handleSave}>
        <div className="settings-section">
          <h3>Dashboard Date Source</h3>
          <div className="mapping-special">
            <label>
              Dashboard date source
              <select value={dashboardDateKey} onChange={(event) => setDashboardDateKey(event.target.value)}>
                {dateOptions.map((key) => <option key={key} value={key}>{COLUMN_LABELS[key] || key}</option>)}
              </select>
            </label>
            <p>Default is Milestone End Date / column AK. Choose Milestone Start Date / column AJ when needed.</p>
          </div>
        </div>

        <div className="settings-section">
          <h3>Promised TAT Settings</h3>
          <p className="section-note">These thresholds drive the On-Time vs Late dashboard. Service combinations are normalized first, so Design & PE and PE & Design use the same TAT setting.</p>
          <div className="tat-settings-grid">
            <label className="tat-default-card">
              <span>Default TAT for unmapped services</span>
              <input
                type="number"
                min="0"
                step="0.25"
                value={tatSettings.defaultTatHours}
                onChange={(event) => setTatSettings((current) => ({ ...current, defaultTatHours: Number(event.target.value) }))}
              />
            </label>
            {Object.keys(tatSettings.serviceTatHours || {}).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((serviceType) => (
              <label key={serviceType}>
                <span className="tat-service-heading">
                  {serviceType}
                  <button type="button" className="link-button danger-link" onClick={() => removeServiceTat(serviceType)}>Remove</button>
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={tatSettings.serviceTatHours?.[serviceType] ?? ''}
                  onChange={(event) => updateServiceTat(serviceType, event.target.value)}
                />
              </label>
            ))}
            <div className="tat-add-card">
              <label>
                <span>Add Service Type</span>
                <input value={newServiceType} onChange={(event) => setNewServiceType(event.target.value)} placeholder="Example: Design & PE" />
              </label>
              <label>
                <span>TAT Hours</span>
                <input type="number" min="0" step="0.25" value={newTatHours} onChange={(event) => setNewTatHours(event.target.value)} />
              </label>
              <button type="button" className="button secondary" onClick={addServiceTat}>Add TAT Mapping</button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Upload Retention</h3>
          <p className="section-note">To control Supabase database size, the app automatically keeps only the latest completed uploads after each successful report upload. The active upload is always preserved.</p>
          <div className="mapping-special">
            <label>
              Keep latest uploads
              <input
                type="number"
                min="1"
                step="1"
                value={tatSettings.uploadRetentionCount || 2}
                onChange={(event) => setTatSettings((current) => ({ ...current, uploadRetentionCount: Math.max(1, Number(event.target.value || 2)) }))}
              />
            </label>
            <p>Recommended: 2 for the Supabase Free plan. Older uploads and their raw/clean rows will be deleted by the cleanup routine.</p>
          </div>
        </div>

        <div className="settings-section">
          <h3>Excel Column Mapping</h3>
          <div className="mapping-grid">
            {editableKeys.map((key) => (
              <label key={key}>
                <span>{COLUMN_LABELS[key] || key}</span>
                <input value={mapping[key] || ''} onChange={(event) => updateColumn(key, event.target.value)} maxLength={3} />
              </label>
            ))}
          </div>
        </div>

        <button type="submit" className="button primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
